import axios from "axios";

// Same-origin /api: the ingress serves the API on whichever domain serves the app
// (preview, live, or a custom domain like radacollab.com). The env URL is only a
// fallback for environments without a browser origin (build/tests).
const ORIGIN =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;
export const API = `${ORIGIN}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

// Attach bearer token fallback (for cross-domain preview testing)
client.interceptors.request.use((config) => {
  const t = localStorage.getItem("rada_session");
  if (t && !config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Public
export const getSite = () => client.get("/config/site").then((r) => r.data);
export const getLinks = () => client.get("/config/links").then((r) => r.data.links);
export const getSections = () => client.get("/config/sections").then((r) => r.data.order);
export const getProducts = (category) =>
  client.get("/products", { params: category ? { category } : {} }).then((r) => r.data.products);
export const getCategories = () => client.get("/categories").then((r) => r.data.categories);
export const getPickupLocations = () => client.get("/pickup-locations").then((r) => r.data.locations);

// Admin
export const adminGetContent = () => client.get("/admin/content").then((r) => r.data);
export const adminPutContent = (data) => client.put("/admin/content", data).then((r) => r.data);
export const adminGetLinks = () => client.get("/admin/links").then((r) => r.data.links);
export const adminPutLinks = (links) => client.put("/admin/links", { links }).then((r) => r.data);
export const adminGetSections = () => client.get("/admin/sections").then((r) => r.data.order);
export const adminPutSections = (order) => client.put("/admin/sections", { order }).then((r) => r.data);

export const adminGetCategories = () => client.get("/admin/categories").then((r) => r.data.categories);
export const adminCreateCategory = (data) => client.post("/admin/categories", data).then((r) => r.data);
export const adminUpdateCategory = (id, data) => client.put(`/admin/categories/${id}`, data).then((r) => r.data);
export const adminDeleteCategory = (id) => client.delete(`/admin/categories/${id}`).then((r) => r.data);
export const adminReorderCategories = (ids) => client.post("/admin/categories/reorder", { ids }).then((r) => r.data);

export const adminGetProducts = () => client.get("/admin/products").then((r) => r.data.products);
export const adminCreateProduct = (data) => client.post("/admin/products", data).then((r) => r.data);
export const adminUpdateProduct = (id, data) => client.put(`/admin/products/${id}`, data).then((r) => r.data);
export const adminDeleteProduct = (id) => client.delete(`/admin/products/${id}`).then((r) => r.data);
export const adminReorderProducts = (items) => client.post("/admin/products/reorder", { items }).then((r) => r.data);

export const adminGetLocations = () => client.get("/admin/locations").then((r) => r.data.locations);
export const adminCreateLocation = (data) => client.post("/admin/locations", data).then((r) => r.data);
export const adminUpdateLocation = (id, data) => client.put(`/admin/locations/${id}`, data).then((r) => r.data);
export const adminDeleteLocation = (id) => client.delete(`/admin/locations/${id}`).then((r) => r.data);

export const adminGetOrders = (status) =>
  client.get("/admin/orders", { params: status ? { status } : {} }).then((r) => r.data.orders);
export const adminSetOrderStatus = (id, status, note) =>
  client.put(`/admin/orders/${id}/status`, { status, note }).then((r) => r.data);
export const adminBulkStatus = (ids, status, note) =>
  client.post("/admin/orders/bulk-status", { ids, status, note }).then((r) => r.data);

export const adminGetBookings = (params) =>
  client.get("/admin/bookings", { params: params || {} }).then((r) => r.data.bookings);
export const getBookingSettings = () => client.get("/booking/settings", { params: { _: Date.now() } }).then((r) => r.data);
export const adminSetBookingSettings = (payload) =>
  client.put("/admin/booking/settings", typeof payload === "object" ? payload : { lead_time_days: payload }).then((r) => r.data);
export const adminAddBlockedDates = (from, to) =>
  client.post("/admin/booking/blocked-dates", { from, to }).then((r) => r.data);
export const adminDeleteBlockedDates = (id) =>
  client.delete(`/admin/booking/blocked-dates/${id}`).then((r) => r.data);
export const adminSetBookingStatus = (id, status) =>
  client.put(`/admin/bookings/${id}/status`, { status }).then((r) => r.data);
export const adminSendCalendarInvite = (id) =>
  client.post(`/admin/bookings/${id}/calendar-invite`).then((r) => r.data);
export const adminBulkBookingStatus = (ids, status) =>
  client.post("/admin/bookings/bulk-status", { ids, status }).then((r) => r.data);
export const adminGetSubscribers = () => client.get("/admin/subscribers").then((r) => r.data.subscribers);
export const adminGetPayments = () => client.get("/admin/payments").then((r) => r.data.payments);
export const adminGetSubAnalytics = () => client.get("/admin/subscribers/analytics").then((r) => r.data);
export const adminExportSubscribers = (format) =>
  client.get(`/admin/subscribers/export`, { params: { format }, responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = format === "xlsx" ? "subscribers.xlsx" : "subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  });
export const adminGetMessages = (status) =>
  client.get("/admin/messages", { params: status ? { status } : {} }).then((r) => r.data.messages);
export const adminSetMessageStatus = (id, status) =>
  client.put(`/admin/messages/${id}/status`, { status }).then((r) => r.data);
export const adminDeleteMessage = (id) => client.delete(`/admin/messages/${id}`).then((r) => r.data);

export const sendMessage = (data) => client.post("/messages", data).then((r) => r.data);

export const adminUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Customer
export const getMyOrders = () => client.get("/me/orders").then((r) => r.data.orders);
export const getMyWishlist = () => client.get("/me/wishlist").then((r) => r.data);
export const addToWishlist = (pid) => client.post(`/me/wishlist/${pid}`).then((r) => r.data);
export const removeFromWishlist = (pid) => client.delete(`/me/wishlist/${pid}`).then((r) => r.data);
export const getRoleContent = () => client.get("/config/roles").then((r) => r.data);
export const adminGetRoles = () => client.get("/admin/roles").then((r) => r.data);
export const adminPutRoles = (data) => client.put("/admin/roles", data).then((r) => r.data);
